import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import {
  BehaviorSubject,
  filter,
  mergeMap,
  Observable,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { SpecialCharacters, UserRoles } from 'src/app/core/enums';
import { UserAdmin } from 'src/app/core/models/admin';
import { Items } from 'src/app/core/models/common/items.model';
import { DeviceService } from 'src/app/core/services/admin/device/device.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { RolePermissionsUtils } from 'src/app/core/utils/role-permissions/role-permissions.utils';
import {
  AddDeviceReturn,
  DeviceAdmin,
  GetAllDevices,
} from '../../../core/models/admin/device-admin.model';
import { TableColumn } from '../../../core/models/common/table-column.model';
import { BANNER_TYPE } from '../../banner/banner.constants';
import { IMAGE_SRC, STATUS_OPTIONS } from '../../shared/shared-constants';
import { AddDeviceComponent } from './add-device/add-device.component';
import { DeleteDeviceComponent } from './delete-device/delete-device.component';
import {
  ColumnIds,
  ColumnText,
  DISPLAY_TEXT,
  EXCEL_COLUMNS,
  EXCEL_FILE_NAME,
  STATUS_ICONS,
} from './device-admin.constants';

@Component({
  selector: 'app-device-admin',
  templateUrl: './device-admin.component.html',
  styleUrls: ['./device-admin.component.scss'],
  standalone: false,
})
export class DeviceAdminComponent implements OnInit {
  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  readonly CONSTANTS = DISPLAY_TEXT;
  readonly displayedColumns = Object.values(ColumnIds);
  readonly COLUMNTEXT = ColumnText;
  readonly STATUS_OPTIONS = STATUS_OPTIONS;
  readonly IMAGE_SRC = IMAGE_SRC;
  readonly statusIcons = STATUS_ICONS;
  readonly SPECIAL_CHARS = SpecialCharacters;
  readonly destroy = inject(DestroyRef);

  public commonUtils = inject(CommonUtils);
  public rolesUtils = inject(RolePermissionsUtils);
  private userService = inject(UserService);
  private readonly dialog = inject(MatDialog);
  private readonly deviceService = inject(DeviceService);
  private destroyRef = inject(DestroyRef);

  tableRowElement!: HTMLCollection;
  devices$!: Observable<GetAllDevices>;
  currentUser!: UserAdmin;
  currentUserRole!: UserRoles;
  userLocation!: string;
  editedDevice!: DeviceAdmin;

  statusOptionChange = false;
  devicesList: DeviceAdmin[] = [];
  dropdownOpen = [false];

  // Table variables
  sortLastUsedAsc = false;
  sortAddedAsc = false;

  employeeIdData: TableColumn = this.commonUtils.createTableColumn(
    ColumnIds.EMPLOYEE_ID,
  );
  driverIdData: TableColumn = this.commonUtils.createTableColumn(
    ColumnIds.DRIVER_ID,
  );
  emailData: TableColumn = this.commonUtils.createTableColumn(ColumnIds.EMAIL);
  lastNameData: TableColumn = this.commonUtils.createTableColumn(
    ColumnIds.LAST_NAME,
  );
  firstNameData: TableColumn = this.commonUtils.createTableColumn(
    ColumnIds.FIRST_NAME,
  );
  lastUsedData: TableColumn = this.commonUtils.createTableColumn(
    ColumnIds.LAST_USED,
  );
  addedData: TableColumn = this.commonUtils.createTableColumn(ColumnIds.ADDED);
  addedByData: TableColumn = this.commonUtils.createTableColumn(
    ColumnIds.ADDED_BY,
  );
  osData: TableColumn = this.commonUtils.createTableColumn(ColumnIds.OS);
  buildData: TableColumn = this.commonUtils.createTableColumn(
    ColumnIds.BUILD_VERSION,
  );
  modelData: TableColumn = this.commonUtils.createTableColumn(ColumnIds.MODEL);
  enabledData: TableColumn = this.commonUtils.createTableColumn(
    ColumnIds.ENABLED,
  );

  dataSource = new MatTableDataSource<DeviceAdmin>();
  resetSelectDropdowns = new BehaviorSubject<boolean>(false);

  ngOnInit(): void {
    this.userService
      .getCurrentUser$()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((user): user is UserAdmin => !!user),
        tap((user) => {
          this.currentUser = user;
          this.currentUserRole = user.roleName;
          this.userLocation = user.location;
          this.clearAllFilters();
        }),
        mergeMap(() => this.getDevices()),
      )
      .subscribe();
  }

  exportExcel(): void {
    this.commonUtils.exportExcel(
      EXCEL_COLUMNS,
      EXCEL_FILE_NAME,
      this.dataSource.filteredData,
      ['id', 'locationCode'],
    );
  }

  search() {
    this.commonUtils.searchFilter(
      [
        this.employeeIdData,
        this.driverIdData,
        this.emailData,
        this.lastNameData,
        this.firstNameData,
        this.lastUsedData,
        this.addedData,
        this.lastUsedData,
        this.addedByData,
        this.osData,
        this.buildData,
        this.modelData,
      ],
      this.dataSource,
      [this.enabledData],
    );
  }

  onStatusChangeForDropdowns(
    $event: string | boolean | unknown[],
    field: TableColumn,
  ) {
    field.ids[0] = $event as unknown as Items;
    this.search();
  }

  clearAllFilters() {
    this.commonUtils.clearAllFilters(this.dataSource, this.dataSource.data, [
      this.employeeIdData.formControl,
      this.driverIdData.formControl,
      this.emailData.formControl,
      this.lastNameData.formControl,
      this.firstNameData.formControl,
      this.lastUsedData.formControl,
      this.addedData.formControl,
      this.addedByData.formControl,
      this.osData.formControl,
      this.buildData.formControl,
      this.modelData.formControl,
      this.enabledData.formControl,
    ]);
    this.sortAddedAsc = false;
    this.sortLastUsedAsc = false;

    this.sortData(ColumnIds.ADDED, false);
    this.resetSelectDropdowns.next(true);
  }

  addDevice() {
    this.dialog
      .open(AddDeviceComponent, {
        disableClose: true,
        data: {
          editMode: false,
          currLocation: this.userLocation,
        },
        width: '50vw',
      })
      .afterClosed()
      .subscribe((newValue: AddDeviceReturn) => {
        if (newValue?.callApi) {
          this.deviceService
            .addDeviceByLocation(newValue.data)
            .pipe(
              take(1),
              switchMap(() => {
                this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
                return this.getDevices();
              }),
            )
            .subscribe();
        }
      });
  }

  editDevice(data: DeviceAdmin) {
    this.deviceService
      .getDeviceById(data.id)
      .pipe()
      .subscribe((deviceData) => {
        this.dialog
          .open(AddDeviceComponent, {
            disableClose: true,
            data: {
              editMode: true,
              currLocation: this.userLocation,
              editData: deviceData.response,
            },
            width: '50vw',
          })
          .afterClosed()
          .subscribe((newValue: AddDeviceReturn) => {
            if (newValue?.callApi) {
              this.deviceService
                .editDeviceByLocation(newValue.data)
                .pipe(
                  take(1),
                  switchMap(() => {
                    this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
                    return this.getDevices();
                  }),
                )
                .subscribe();
            }
          });
      });
  }

  deleteDevice(device: DeviceAdmin) {
    this.dialog
      .open(DeleteDeviceComponent, {
        disableClose: true,
        data: {
          id: device?.id,
          driverId: device?.driverId,
          emailId: device?.email,
          modelNo: device?.model,
        },
        width: '20rem',
      })
      .afterClosed()
      .subscribe((resp) => {
        if (resp) {
          const data = this.dataSource.data.find((value) => value.id === resp);
          if (data) {
            this.deviceService
              .deleteDevice(data.id.toString())
              .pipe(
                take(1),
                switchMap(() => {
                  this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
                  return this.getDevices();
                }),
              )
              .subscribe();
          }
        }
      });
  }

  sortData(column: string, flipValue = true) {
    if (flipValue) {
      if (column === ColumnIds.LAST_USED) {
        this.sortLastUsedAsc = !this.sortLastUsedAsc;
      }

      if (column === ColumnIds.ADDED) {
        this.sortAddedAsc = !this.sortAddedAsc;
      }
    }

    this.dataSource.data.sort((a: DeviceAdmin, b: DeviceAdmin) => {
      switch (column) {
        case ColumnIds.LAST_USED:
          return this.convertData(
            a.lastLoggedIn,
            b.lastLoggedIn,
            this.sortLastUsedAsc,
          );
        case ColumnIds.ADDED:
          return this.convertData(
            a?.insertTime ?? '',
            b?.insertTime ?? '',
            this.sortAddedAsc,
          );
        default:
          return 0;
      }
    });
    this.dataSource._updateChangeSubscription();
  }

  get isFilterEnabled() {
    return (
      this.employeeIdData.formControl.value !== '' ||
      this.driverIdData.formControl.value !== '' ||
      this.emailData.formControl.value !== '' ||
      this.lastNameData.formControl.value !== '' ||
      this.firstNameData.formControl.value !== '' ||
      this.sortLastUsedAsc === true ||
      this.sortAddedAsc === true ||
      this.addedByData.formControl.value !== '' ||
      this.osData.formControl.value !== '' ||
      this.buildData.formControl.value !== '' ||
      this.modelData.formControl.value !== '' ||
      this.enabledData.ids.length > 0
    );
  }

  private convertData(a: string, b: string, sortAsc: boolean) {
    const aVal = a ? a.toString().toLowerCase() : '';
    const bVal = b ? b.toString().toLowerCase() : '';
    if (sortAsc) {
      return bVal.localeCompare(aVal);
    } else {
      return aVal.localeCompare(bVal);
    }
  }

  private getDevices() {
    return this.deviceService
      .getAllDevicesByLocation(this.currentUser.location)
      .pipe(
        take(1),
        tap((getDevices) => {
          this.dataSource.data =
            getDevices?.response?.devices.map((val) => {
              return {
                employeeId: val.employeeId?.toUpperCase(),
                driverId: val.driverId?.toUpperCase(),
                email: val.email?.toUpperCase(),
                lastName: val.lastName?.toUpperCase(),
                firstName: val.firstName?.toUpperCase(),
                lastLoggedIn: val.lastLoggedIn,
                insertTime: val.insertTime,
                addedBy: val.addedBy?.toUpperCase(),
                os: val.os?.toUpperCase(),
                buildVersion: val.buildVersion?.toUpperCase(),
                model: val.model?.toUpperCase(),
                enabled: val.enabled,
                id: val.id,
                locationCode: val.locationCode?.toUpperCase(),
              };
            }) || [];
          this.dataSource.sort = this.sort;
          this.dataSource.filterPredicate = this.commonUtils.createFilter();

          this.sortData(ColumnIds.ADDED, false);
        }),
      );
  }
}
